import { supabase } from '../../../services/supabase/client'
import type { Database } from '../../../types/database.types'

export type Notification = Database['public']['Tables']['notifications']['Row']
export type NotificationType = Database['public']['Enums']['notification_type']

export class NotificationService {
  /**
   * Get all notifications for the currently authenticated user,
   * ordered newest first
   */
  async getNotifications(): Promise<Notification[]> {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) throw new Error('Not authenticated')

    const { data, error } = await supabase
      .from('notifications')
      .select('*')
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data ?? []
  }

  /**
   * Get the count of unread notifications for the current user
   */
  async getUnreadCount(): Promise<number> {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) return 0

    const { count, error } = await supabase
      .from('notifications')
      .select('*', { count: 'exact', head: true })
      .eq('recipient_id', user.id)
      .eq('is_read', false)

    if (error) throw error
    return count ?? 0
  }

  /**
   * Mark a single notification as read
   */
  async markAsRead(id: string): Promise<void> {
    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('id', id)

    if (error) throw error
  }

  /**
   * Mark all notifications as read for the current user
   */
  async markAllAsRead(): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser()

    if (!user) throw new Error('Not authenticated')

    const { error } = await supabase
      .from('notifications')
      .update({ is_read: true, read_at: new Date().toISOString() })
      .eq('recipient_id', user.id)
      .eq('is_read', false)

    if (error) throw error
  }
}

export const notificationService = new NotificationService()
